import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {Button} from '../button/button';
import {MinimizeButton} from '../button/minimize-button/minimize-button';
import {Card} from '../card/card';
import {CueCard} from '../cue-card/cue-card';
import {Body} from '../typography/body';
import {useResizeObserver} from '../utils/hooks/use-resize-observer';

import {AnimatingBookmark} from './animating-bookmark';
import {ChecklistCloseButton} from './checklist-close-button';
import {getCueCardsForChecklist} from './helpers';
import {OnboardingChecklistHeader} from './onboarding-checklist-header';
import type {Checklist} from './types';

import styles from './onboarding-checklist.module.css';

export const STACK_WIDTH_THRESHOLD = 700;

type Props = {
    checklist: Checklist;
    completedCount: number;
    totalCount: number;
    checklistIsComplete: boolean;
    onMinimize?: () => void;
    onClose?: () => void;
    history: RouterHistory;
    title?: string;
    description?: string;
    onExploreFurtherClick?: () => void;
    hasCollapsedButton?: boolean;
};

export function OnboardingChecklist(props: Props) {
    const {title = 'Get started with Nutshell', hasCollapsedButton = false} = props;

    const [isCollapsed, setIsCollapsed] = React.useState(props.checklistIsComplete);

    const containerRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const [width] = useResizeObserver(containerRef.current);
    const cueCards = getCueCardsForChecklist(props.checklist.items);

    return (
        <Card hasBorder={true} fitHeight={true}>
            {/* @ts-expect-error */}
            <div ref={containerRef} className={styles.container}>
                <div className={styles.minimize}>
                    {props.onMinimize && (
                        <MinimizeButton onClick={props.onMinimize} size={17} variant='medium' />
                    )}
                </div>
                <div className={styles.close}>
                    {props.onClose && <ChecklistCloseButton onClose={props.onClose} />}
                </div>
                <AnimatingBookmark />
                <OnboardingChecklistHeader
                    checklist={props.checklist}
                    completedCount={props.completedCount}
                    totalCount={props.totalCount}
                    checklistIsComplete={props.checklistIsComplete}
                    title={title}
                    expandProps={
                        hasCollapsedButton
                            ? {
                                  isExpanded: !isCollapsed,
                                  onToggleExpand: () => setIsCollapsed(!isCollapsed),
                              }
                            : undefined
                    }
                />
                {!isCollapsed && (
                    <>
                        {!props.checklistIsComplete && props.description && (
                            <div className='mb-16'>
                                <Body>{props.description}</Body>
                            </div>
                        )}
                        <div
                            className={
                                width && width < STACK_WIDTH_THRESHOLD
                                    ? styles['cards--column']
                                    : styles.cards
                            }
                        >
                            {cueCards.map((cueCard) => (
                                <CueCard
                                    key={cueCard.settingsKey}
                                    history={props.history}
                                    {...cueCard}
                                />
                            ))}
                        </div>
                        {props.onExploreFurtherClick && (
                            <div className='mt-16'>
                                <Button
                                    onClick={props.onExploreFurtherClick}
                                    variant='text-primary'
                                    size='big'
                                    noPadding={true}
                                >
                                    Explore even further
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Card>
    );
}
